import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, Users, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import {
  useSharedListRealtime,
  type SharedPackingItem,
} from '@/hooks/useSharedPackingList';

export default function JoinPackingList() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [listId, setListId] = useState<string | null>(null);
  const [destination, setDestination] = useState('');
  const [items, setItems] = useState<SharedPackingItem[]>([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      sessionStorage.setItem('pending_join_code', code ?? '');
      navigate(`/auth?next=${encodeURIComponent(`/join/${code}`)}`, { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      const { data: joinedId, error: joinError } = await supabase.rpc('join_packing_list', {
        _share_code: code ?? '',
        _display_name: user.email ?? null,
      });

      if (cancelled) return;

      if (joinError || !joinedId) {
        setError(joinError?.message ?? 'This invite code is no longer valid.');
        setStatus('error');
        return;
      }

      const { data: list, error: listError } = await supabase
        .from('shared_packing_lists')
        .select('id, destination, items')
        .eq('id', joinedId as string)
        .maybeSingle();

      if (cancelled) return;

      if (listError || !list) {
        setError('We could not load this packing list.');
        setStatus('error');
        return;
      }

      setListId(list.id);
      setDestination(list.destination);
      setItems(Array.isArray(list.items) ? (list.items as unknown as SharedPackingItem[]) : []);
      setStatus('ready');
      sessionStorage.removeItem('pending_join_code');
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, user, code, navigate]);

  const handleRealtime = useCallback((next: SharedPackingItem[]) => setItems(next), []);
  useSharedListRealtime(listId, handleRealtime);

  const toggleItem = async (itemId: string) => {
    if (!listId) return;
    const next = items.map((item) =>
      item.id === itemId ? { ...item, packed: !item.packed } : item
    );
    setItems(next);
    const { error: updateError } = await supabase
      .from('shared_packing_lists')
      .update({ items: next as unknown as never })
      .eq('id', listId);
    if (updateError) {
      toast.error('Could not save that change');
      setItems(items);
    }
  };

  if (status === 'loading' || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Invite not found</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button className="w-full" onClick={() => navigate('/dashboard')}>
              Go to dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const packed = items.filter((i) => i.packed).length;

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <Helmet>
        <title>Shared packing list | OdyVoy</title>
        <meta name="description" content="Collaborate on a shared OdyVoy packing list with your travel buddies." />
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="max-w-2xl mx-auto space-y-6">
        <header className="space-y-2">
          <Badge variant="secondary" className="gap-1">
            <Users className="h-3 w-3" /> Shared list
          </Badge>
          <h1 className="text-3xl font-bold">Packing together</h1>
          <p className="text-muted-foreground flex items-center gap-1 text-sm">
            <MapPin className="h-4 w-4" /> {destination} · {packed}/{items.length} packed
          </p>
        </header>

        <Card>
          <CardContent className="p-4 space-y-2">
            {items.length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center">
                The list owner hasn't added items yet — check back shortly.
              </p>
            )}
            {items.map((item) => (
              <label
                key={item.id}
                className="flex items-center gap-3 min-h-12 px-2 rounded-md hover:bg-muted/50 cursor-pointer"
              >
                <Checkbox checked={item.packed} onCheckedChange={() => toggleItem(item.id)} />
                <span className={item.packed ? 'line-through text-muted-foreground' : ''}>
                  {item.name}
                </span>
                {item.essential && (
                  <Badge variant="outline" className="ml-auto text-xs">
                    Essential
                  </Badge>
                )}
              </label>
            ))}
          </CardContent>
        </Card>

        <Button className="w-full" onClick={() => navigate('/dashboard')}>
          Plan my own trip
        </Button>
      </div>
    </div>
  );
}
